import React from 'react';
import { useTranslation } from 'react-i18next';
import { FaPlus } from 'react-icons/fa';
import { useRecoilState, useResetRecoilState } from 'recoil';

import { COLOURS } from '@tq/ui-components/dist/pp2/theme';
import {
  Container,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  TabText,
  Button,
  Flex,
} from '@tq/ui-components';

import { PageHeading } from 'components';
import { useNavigate } from 'hooks';
import {
  manageConsulteeActiveTab,
  organisationIdState,
  organisationRequestState,
  useResetGetConsulteeRequestState,
} from 'store';
import { ManageOrganisations, ManageTeams, ManageUsers } from './components';
import { useManageTabLabels } from './hooks';
import { tabPanelStyle, tabPanelsStyle } from './styles';

export const ManageConsultees: React.FC = () => {
  /**
   * Returns the main container UI for maintaining Organisations, Teams and Consultees by TQ Admins.
   *
   * @remarks
   * TQ admins only
   */
  const { t } = useTranslation('manage_consultees');
  const tabLabels = useManageTabLabels();
  const { navigateToCreateOrganisation } = useNavigate();
  const resetManageConsulteesRequest = useResetGetConsulteeRequestState();
  const resetCreatedOrganisationId = useResetRecoilState(organisationIdState);
  const resetCreatedOrganisationRequest = useResetRecoilState(organisationRequestState);
  const [activeTab, setActiveTab] = useRecoilState(manageConsulteeActiveTab);

  const handleAddOrganisationClick = () => {
    resetCreatedOrganisationId();
    resetCreatedOrganisationRequest();
    navigateToCreateOrganisation();
    resetManageConsulteesRequest();
  };

  return (
    <Container px={{ base: 0, lg: 4, xl: 0 }}>
      <Flex justifyContent='space-between'>
        <PageHeading heading={t('manage_consultees', 'Manage consultees')} />
        <Button
          rightIcon={<FaPlus />}
          onClick={handleAddOrganisationClick}
          variant='secondary'
        >
          {t('add_organisation_button_text', 'Add new organisation')}
        </Button>
      </Flex>
      <Tabs
        defaultIndex={activeTab}
        variant='enclosed'
        p={0}
        mt={8}
        mb={12}
        mx={{ base: -5, lg: 0 }}
        isManual={true}
      >
        <TabList color={COLOURS.blue[600]} sx={{ '.tqc-badge': { ml: 3 } }}>
          {tabLabels.map((tabLabel, index) => (
            <Tab
              key={tabLabel}
              value={index}
              onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) =>
                setActiveTab(Number(e.currentTarget.value))
              }
            >
              <TabText fontSize={18}>{tabLabel}</TabText>
            </Tab>
          ))}
        </TabList>
        <TabPanels {...tabPanelsStyle}>
          <TabPanel {...tabPanelStyle}>
            <ManageOrganisations />
          </TabPanel>
          <TabPanel {...tabPanelStyle}>
            <ManageTeams />
          </TabPanel>
          <TabPanel {...tabPanelStyle}>
            <ManageUsers />
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Container>
  );
};
